import { useCallback, useContext, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import { formatDate, splitDateTime } from "capitalrx-utilities";
import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { getFormattedValue } from "capitalrx-components-2.0/lib/general/Table/helpers";

import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { Logo, PrintableWrapper, ThemedButton } from "@/App/components";
import { getCustomerSupportNumber } from "@/App/utils/utils";
import { ExperienceContext } from "@/App/context";
import { useGetMedicalClaimDetails } from "@/App/api/claims";

import "./PrintClaims.scss";

export const PrintMedicalClaimDetailsPage = () => {
  const { i18n, t: translate } = useTranslation();
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { customerSupportPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = getCustomerSupportNumber(memberEligibility, customerSupportPhoneNumber);
  const { first_name, last_name, external_member_id, birth_date } = memberEligibility.member;
  const language: string = i18n.language ?? "en";
  const now = DateTime.now().setLocale(language);
  const formattedDate = now.toLocaleString(DateTime.DATE_FULL);
  const printRef = useRef();
  const [searchParams] = useSearchParams();

  const claimId = searchParams.get("claimId");
  const personCode = searchParams.get("personCode");
  const { data: medicalClaimData, isLoading: isLoadingMedicalClaim } = useGetMedicalClaimDetails({ id: +claimId, person_code: personCode });
  const isApproved = medicalClaimData?.status !== "pended";

  const serviceDate = DateTime.fromISO(medicalClaimData?.minimum_service_date);
  const serviceDateMonth = serviceDate.toFormat("LLLL").toLowerCase();
  const formattedServiceDate = `${translate(`PRINT_CLAIM_DETAILS_PAGE.${serviceDateMonth}`)} ${serviceDate.day}, ${serviceDate.year}`;
  const receivedDate = DateTime.fromISO(medicalClaimData?.submission_date);
  const receivedDateMonth = receivedDate.toFormat("LLLL").toLowerCase();
  const formattedReceivedDate = `${translate(`PRINT_CLAIM_DETAILS_PAGE.${receivedDateMonth}`)} ${receivedDate.day}, ${receivedDate.year}`;

  const statusMap = {
    pended: translate("CLAIMS.statusOptions.inProgress"),
    partially_paid: translate("CLAIMS.statusOptions.partiallyApproved"),
    paid: translate("CLAIMS.statusOptions.approved"),
    denied: translate("CLAIMS.statusOptions.denied"),
  };

  let totalBilled = 0;
  let totalAllowedByPlan = 0;
  let totalPlanPaid = 0;
  let totalPatientPay = 0;

  if (medicalClaimData?.service_line_items) {
    for (const lineItem of medicalClaimData.service_line_items) {
      totalBilled = totalBilled + lineItem.line_item_charge_amount;
      totalAllowedByPlan = totalAllowedByPlan + lineItem.repriced_allowed_amount;
      totalPlanPaid = totalPlanPaid + lineItem.plan_pay_amount;
      totalPatientPay = totalPatientPay + lineItem.patient_pay_amount;
    }
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const onPrint = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.CLAIM_DETAILS.printMedicalClaimDetails);
    handlePrint();
  }, [handlePrint]);

  const claimIdFormatted = useMemo(() => {
    if (medicalClaimData == null) {
      return "";
    }
    return String(medicalClaimData.id).padStart(3, "0");
  }, [medicalClaimData]);

  const hasToothCodes = medicalClaimData?.service_line_items?.some((line_item) => line_item?.tooth_code_surface && line_item?.tooth_code_surface.length > 0);

  return (
    <LoadingGroup loading={isLoadingMedicalClaim}>
      <PrintableWrapper ref={printRef}>
        <Stack style={{ height: "100%" }}>
          <Shelf alignment="top">
            <Logo />
            <Shelf.Spacer />
            <ThemedButton
              testId="print-medical-claims-history"
              className="print-button"
              onClick={onPrint}
              appearance="text"
              icon="print"
            />
          </Shelf>
          <div>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.memberName")}</Semibold> {first_name} {last_name}
            </Body>
            {
              !isViewingDataholder && <Body>
                <Semibold>{translate("PRINT_CLAIMS_PAGE.dateOfBirth")}</Semibold> {formatDate(birth_date, "MM/DD/YYYY")}
              </Body>
            }
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.memberId")}</Semibold> {external_member_id}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIMS_PAGE.personCode")}</Semibold> {personCode}
            </Body>
          </div>
          <Shelf>
            <Body size="medium">
              <Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.claimSummary")}</Semibold>
            </Body>
            <Shelf.Spacer />
            <Body>{`${translate("PRINT_CLAIMS_PAGE.prepared")} ${formattedDate}`}</Body>
          </Shelf>
          <div>
            <Body>
              <Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.claimId")}</Semibold>{claimIdFormatted}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.claimStatus")}</Semibold> {statusMap[medicalClaimData?.status]}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.dateOfService")}</Semibold> {formattedServiceDate}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.billingProvider")}</Semibold> {medicalClaimData?.billing_provider_first_name ? medicalClaimData?.billing_provider_first_name : null} {medicalClaimData?.billing_provider_last_name_or_organizational_name}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.renderingProvider")}</Semibold> {medicalClaimData?.rendering_provider_first_name ? medicalClaimData?.rendering_provider_first_name : null} {medicalClaimData?.rendering_provider_last_name_or_organizational_name}
            </Body>
            <Body>
              <Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.received")}</Semibold> {formattedReceivedDate}
            </Body>
          </div>
          <LoadingGroup.Placeholder
            fallback={
              <Shelf alignment="center">
                <Stack alignment="center">
                  <Spinner size="large" />
                </Stack>
              </Shelf>
            }
          >
            <table className="claims-history-table">
              <thead>
                <tr>
                  <th><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.serviceName")}</Semibold></th>
                  <th><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.procedureCode")}</Semibold></th>
                  {hasToothCodes && <th><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.toothCodes")}</Semibold></th>}
                  <th className="date"><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.serviceDate")}</Semibold></th>
                  {medicalClaimData?.status != "pended" && (
                    <>
                      <th className="currency"><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.billedByProvider")}</Semibold></th>
                      <th className="currency"><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.allowedByPlan")}</Semibold></th>
                      <th className="currency"><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.planPaid")}</Semibold></th>
                      {isApproved && <th className="currency"><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.patientPay")}</Semibold></th>}
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {
                  medicalClaimData?.service_line_items.map((line_item, index) => {
                    let displayedCategory = line_item.service_category;

                    if (line_item.preventive_category) {
                      displayedCategory = `${line_item.preventive_category} (${translate("PRINT_CLAIM_DETAILS_PAGE.preventive")})`;
                    }

                  return (
                    <tr key={index}>
                      <td>{getFormattedValue(displayedCategory)}</td>
                      <td>{line_item.procedure_code}</td>
                      {hasToothCodes && <td>{line_item.tooth_code_surface.join(", ")}</td>}
                      <td className="date">{splitDateTime(line_item.service_date)[0]}</td>
                      {medicalClaimData?.status !== "pended" && (
                        <>
                          <td className="currency">${getFormattedValue(line_item.line_item_charge_amount)}</td>
                          <td className="currency">${getFormattedValue(line_item.repriced_allowed_amount)}</td>
                          <td className="currency">${getFormattedValue(line_item.plan_pay_amount)}</td>
                        </>
                      )}
                      {isApproved && <td className="currency">${getFormattedValue(line_item.patient_pay_amount)}</td>}
                    </tr>
                  );
                })}
                {medicalClaimData?.status != "pended" && (
                  <>
                    <tr className="totals">
                      <td><Semibold>{translate("PRINT_CLAIM_DETAILS_PAGE.totals")}</Semibold></td>
                      <td></td>
                      <td></td>
                      <td className="currency"><Semibold>${getFormattedValue(totalBilled)}</Semibold></td>
                      <td className="currency"><Semibold>${getFormattedValue(totalAllowedByPlan)}</Semibold></td>
                      <td className="currency"><Semibold>${getFormattedValue(totalPlanPaid)}</Semibold></td>
                      {isApproved && (<td className="currency"><Semibold>${getFormattedValue(totalPatientPay)}</Semibold></td>)}
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </LoadingGroup.Placeholder>
          <Stack.Spacer />
          <Stack spacing="small">
            <Body size="medium"><Semibold>{translate("PRINT_CLAIMS_PAGE.customerSupport")}</Semibold></Body>
            <Body testId="customer-support-number">
              {translate("PRINT_CLAIMS_PAGE.contactCustomerCareAt")} <Semibold>{customerSupportNumber}</Semibold>.
            </Body>
          </Stack>
        </Stack>
      </PrintableWrapper>
    </LoadingGroup>
  );
};
